import { Google_SignupWithRef } from "@/apis/api";
import { withGoogleAccountChooser } from "@/utils/googleAuthUrl";
import { canUseAppAuth, isNativeApp, openGoogleAuth } from "@/utils/nativeApp";

/*
  Google 登入開小視窗（2026-10-21，電腦版的登入對話框）。

  電腦版在登入對話框按「以 Google 帳號繼續」：Google 開在左上角的小視窗，選完帳號小視窗自己關掉，
  原本的頁面直接變成已登入 —— 跟 Figma 一樣。手機、App、小視窗被瀏覽器擋下時，照舊整頁跳轉。

  流程跟整頁跳轉是同一條，後端不用改：
    小視窗 → Google 選帳號 → 後端 Google/signin-google（寫登入 Cookie）→ 前端 /loginSuccess?guid=…
  /loginSuccess（views/LoginSuccess.vue）發現自己是小視窗，就用 guid 換 access token、
  在 localStorage 留下結果、關掉自己；原視窗聽到 storage 事件就更新登入狀態。

  為什麼用 localStorage 傳結果，不用 window.opener.postMessage：
  Google 的登入頁有 Cross-Origin-Opener-Policy，小視窗經過 Google 之後跟原視窗的連結會被瀏覽器切斷
  （小視窗裡 window.opener 變 null、原視窗看 popup.closed 直接是 true），只有同網域共用的 localStorage 靠得住。
*/
const POPUP_NAME = "taro-google-login";
const PENDING_KEY = "taro_google_popup_pending";
const RESULT_KEY = "taro_google_popup_result";
const PENDING_TTL = 10 * 60 * 1000;

/**
 * 電腦版才開小視窗：手機開小視窗其實是開新分頁，體驗比整頁跳轉差；App 要走系統瀏覽器。
 * 內嵌在社群、AI 站的對話框裡（views/EmbedLogin.vue）時，視窗寬度量到的是 iframe 自己（約 440px），
 * 會被當成手機 —— 改看螢幕寬度
 */
export function canUseGooglePopup() {
  if (isNativeApp()) return false;
  try {
    const embedded = window.self !== window.top;
    const width = embedded ? (window.screen && window.screen.width) || 0 : window.innerWidth;
    return window.matchMedia("(pointer: fine)").matches && width >= 960;
  } catch (e) {
    return false;
  }
}

/**
 * 一定要在點擊當下同步呼叫（先開空白小視窗，網址拿到再換過去），不然會被瀏覽器當成彈出廣告擋掉。
 * 網址給空字串（一樣是空白頁）：全站的「外部連結提醒」（plugins/external-link-guard.js）會攔 window.open，
 * 寫 about:blank 會被當成外部網站跳出提醒，空字串不算
 */
function openPopupWindow() {
  const left = Math.max(0, (window.screenX || 0) + 40);
  const top = Math.max(0, (window.screenY || 0) + 40);
  try {
    return window.open("", POPUP_NAME, `width=480,height=640,left=${left},top=${top},resizable=yes,scrollbars=yes`);
  } catch (e) {
    return null;
  }
}

/** 小視窗那一端：這次載入是不是登入對話框開的 Google 小視窗 */
export function isGooglePopupLanding() {
  try {
    const at = Number(localStorage.getItem(PENDING_KEY) || 0);
    return at > 0 && Date.now() - at < PENDING_TTL;
  } catch (e) {
    return false;
  }
}

/** 小視窗那一端：把結果留給原視窗，關掉自己（瀏覽器不讓關的話，畫面上會顯示「登入成功」） */
export function finishGooglePopup(ok) {
  try {
    localStorage.removeItem(PENDING_KEY);
    localStorage.setItem(RESULT_KEY, JSON.stringify({ ok: !!ok, at: Date.now() }));
  } catch (e) {
    /* 略過 */
  }
  window.close();
}

/**
 * 登入對話框按「以 Google 帳號繼續」。
 *
 * @param {object} options
 * @param {string} [options.refc] 推薦碼（Google 新註冊的會員要掛到推薦人底下）
 * @param {boolean} options.popup 想開小視窗（對話框才傳 true；電腦版才真的開）
 * @param {string} [options.returnUrl] 整頁跳轉時，登入完要回到哪一頁
 * @param {function} [options.onIdle] 使用者回到原視窗（例如把小視窗關了）時呼叫，讓按鈕先恢復
 * @param {function} [options.navigate] 整頁跳轉怎麼換頁（內嵌在社群、AI 站時要換外層的頁面，不是 iframe）
 * @returns {{ promise: Promise<"signed-in"|"failed"|"redirected">, cancel: function }}
 *   小視窗：登入完 resolve "signed-in"；整頁跳轉：resolve "redirected"（頁面接著就離開了）
 */
export function startGoogleLogin({ refc, popup: wantPopup, returnUrl, onIdle, navigate } = {}) {
  const popup = wantPopup && canUseGooglePopup() ? openPopupWindow() : null;
  let cleanup = () => {};

  if (!popup) {
    try {
      localStorage.removeItem(PENDING_KEY);
      if (returnUrl) localStorage.setItem("redirectAfterLogin", returnUrl);
      localStorage.removeItem("_token");
    } catch (e) {
      /* 略過 */
    }
    const promise = Google_SignupWithRef(refc, canUseAppAuth())
      .then((res) => {
        const url = withGoogleAccountChooser(res.data);
        return navigate ? navigate(url) : openGoogleAuth(url);
      })
      .then(() => "redirected");
    return { promise, cancel: () => {} };
  }

  try {
    localStorage.removeItem(RESULT_KEY);
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch (e) {
    /* 略過 */
  }

  const result = new Promise((resolve) => {
    const onStorage = (e) => {
      if (e.key !== RESULT_KEY || !e.newValue) return;
      let r = null;
      try {
        r = JSON.parse(e.newValue);
        localStorage.removeItem(RESULT_KEY);
      } catch (err) {
        /* 略過 */
      }
      cleanup();
      resolve(r && r.ok ? "signed-in" : "failed");
    };
    // 回到原視窗：可能是把小視窗關了，按鈕先恢復；之後小視窗才登入完成的話照樣會登入
    const onFocus = () => setTimeout(() => onIdle && onIdle(), 800);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    cleanup = () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  });

  const promise = Google_SignupWithRef(refc, false)
    .then((res) => {
      popup.location.href = withGoogleAccountChooser(res.data);
      return result;
    })
    .catch((err) => {
      cleanup();
      try {
        localStorage.removeItem(PENDING_KEY);
        if (!popup.closed) popup.close();
      } catch (e) {
        /* 略過 */
      }
      throw err;
    });

  return {
    promise,
    // 對話框關掉了：不再等結果（小視窗如果還開著，登入完成後會留在 /loginSuccess 頁）
    cancel: () => {
      cleanup();
      try {
        localStorage.removeItem(PENDING_KEY);
      } catch (e) {
        /* 略過 */
      }
    },
  };
}
